import EmailOutlined from "@mui/icons-material/EmailOutlined";
import RemoveRedEyeOutlined from "@mui/icons-material/RemoveRedEyeOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { IncidentWorkItemWithContext } from "@thalamos/vision-common";
import { getWorkflowTranslationContext, tk } from "@thalamos/vision-common";

import { TypographyI18N } from "../../components/index.js";

export const SHARE_OPTIONS = {
  EMAIL: "shareByEmail",
  WITHOUT_EMAIL: "shareWithoutEmail",
  UNABLE: "unableToShare",
} as const;

export type OptionsModelProps =
  (typeof SHARE_OPTIONS)[keyof typeof SHARE_OPTIONS];

export function ShareOptionsPage({
  placeName,
  onSelectOption,
  incidentWorkItem,
}: {
  placeName: string;
  onSelectOption: (option: OptionsModelProps) => void;
  incidentWorkItem: IncidentWorkItemWithContext;
}) {
  const { t } = useTranslation();
  const tOptions = {
    context: getWorkflowTranslationContext({ incidentWorkItem }),
  };

  return (
    <Box sx={{ padding: "1.5rem" }}>
      <Stack spacing={3}>
        {/* Sharing with */}
        <Box>
          <TypographyI18N
            translationKey={tk("sharePage.shareWith")}
            sx={{
              fontSize: "1rem",
              fontWeight: 400,
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{ mt: 0.5, fontWeight: 600 }}
          >
            {placeName}
          </Typography>
        </Box>

        {/* Share by Email */}
        <Box>
          <Button
            data-testid="share-option-email"
            variant="contained"
            color="primary"
            fullWidth
            endIcon={<EmailOutlined />}
            onClick={() => {
              onSelectOption("shareByEmail");
            }}
          >
            {t("sharePage.shareByEmail")}
          </Button>
          <TypographyI18N
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, textAlign: "center" }}
            translationKey={tk("sharePage.sendFormsViaEmail")}
            translationOptions={tOptions}
          />
        </Box>

        {/* Other options */}
        <Box>
          <TypographyI18N
            translationKey={tk("sharePage.otherOptions")}
            variant="subtitle2"
            color="text.secondary"
            sx={{
              mb: 1,
            }}
          />
          <Button
            variant="outlined"
            data-testid="share-option-share-code"
            fullWidth
            endIcon={<RemoveRedEyeOutlined />}
            onClick={() => {
              onSelectOption("shareWithoutEmail");
            }}
          >
            {t("sharePage.shareWithoutEmailOption")}
          </Button>
          <TypographyI18N
            translationKey={tk("sharePage.posUniqueCode")}
            translationOptions={tOptions}
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, textAlign: "center" }}
          />
        </Box>

        {/* Unable to share message */}
        <Button
          variant="text"
          data-testid="share-option-unable-to-share"
          color="primary"
          onClick={() => {
            onSelectOption("unableToShare");
          }}
          sx={{
            mt: 2,
            textAlign: "center",
            fontWeight: "bold",
            textTransform: "uppercase",
            minWidth: "auto",
          }}
        >
          {t("sharePage.unableToShareOption")}
        </Button>
      </Stack>
    </Box>
  );
}
